import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { AngebotStatus, AngebotTreffer, AngebotTrefferSeite, Angebot, ApiClient,
              KundeListe as KundeListeEintrag, Unternehmen } from "@kundeninfo/api-client";
import { AngebotDetail, angebotFormularFelder, angebotVorschauWerte,
         useAngebotsUmfeld } from "../components/AngebotDetail.js";
import { Modal, ModalFuss, darfAbbrechen, Objektliste, Objektzeile, useBestaetigung }
  from "@kundeninfo/ui";
import { Auswahl, Hinweis } from "@kundeninfo/ui/me-design";
import { useAngebotAnlegen } from "../components/angebotAnlegen.js";
import { apiMeldung, entwuerfeGleich } from "../components/vorgangsdetail.js";
import { KundenListe } from "./KundenListe.js";
import type { KundenFehler } from "./KundenListe.js";
import type { AngeboteClient } from "./KundeAngebote.js";
import type { VerlassenWaechter } from "../router.js";
import { formatBetrag, formatDatum } from "../format.js";
import { ANGEBOT_STATUS_ART, ANGEBOT_STATUS_LABEL } from "../vorgangsstatus.js";
import { ANGEBOTS_FILTER, statusZuFilter } from "../bereichsfilter.js";
import type { AngebotsFilter } from "../bereichsfilter.js";
import { Filterkopf, UnternehmensChips, sichtFuer } from "../components/Filterkopf.js";
import { useStabileListe } from "../components/stabileListe.js";
import type { FilterChip } from "../components/Filterkopf.js";
import { KiKnopf } from "../components/meHuellen.js";
import { ObjektVerweis } from "../components/objektPanel/ObjektVerweis.js";
import { HistoricalChip } from "../components/historical/HistoricalChip.js";
import { HistoricalCreateForCustomer } from "../components/historical/HistoricalRecordPanel.js";
import { KindFilterSelect, kindParam } from "../components/historical/KindFilterSelect.js";
import { HISTORICAL_TEXT } from "../components/historical/texts.js";
import type { KindFilter } from "../components/historical/texts.js";
import { LEAVE_TEXT } from "./leaveTexts.js";
import { SUCCESSION_TEXT } from "../components/offerSuccession/texts.js";
import "./BereichAngebote.css";

/**
 * Der Angebots-BEREICH: Spaltenliste links (Filterkopf, Angebotszeilen, Blätterung),
 * vollwertiges Lese-Detail rechts — die Schwester von views/BereichArtikel.tsx und (ab
 * Aufgabe 6) views/BereichAuftraege.tsx, Zeile für Zeile nach demselben Muster gebaut.
 *
 * Die ANORDNUNG ist zweimal gewechselt und steht jetzt wieder, wo sie anfing: Spalte und
 * Detail bis zum 07.09.2026, dann „Filterkopf statt zweiter Spalte" (alles untereinander in
 * voller Breite), und seit dem 23.09.2026 wieder Spalte und Detail (Entscheid Dirk am
 * Live-System). Vom Umbau des 07.09. BLEIBT, was sich dabei als richtig erwiesen hat: der
 * Filterkopf als EIN Baustein für alle vier Bereiche (er steht jetzt oben IN der Spalte,
 * siehe `lage="spalte"`), der Filter in der Adresse und die Detail-Bausteine. Was geht, ist
 * allein das Untereinander — und mit ihm der bereichseigene Sprung-Mechanismus
 * (components/Bereichsfaltung.tsx), denn die Faltung auf schmalem Gerät macht wieder der
 * Rahmen.
 *
 * Die ZEILEN zeichnet seit dem 23.09.2026 der gemeinsame Baustein `Objektliste`/
 * `Objektzeile` aus @kundeninfo/ui — dieselbe Zeile wie im Artikel-Bereich und im
 * Artikelstamm der Verwaltung.
 *
 * **Warum ein Hook und keine Komponente.** Historisch, und die Gründe tragen weiter: Der
 * Bereich füllt ZWEI Bereiche des Rahmens — die Listenspalte (`MeSchale.list`) und die
 * Detailfläche (`MeSchale.detail`). Beide
 * teilen sich einen Zustand — die Trefferseite, den gewählten Vorgang, das offene
 * Formular. Eine Komponente kann ihre Ausgabe nicht auf zwei Eigenschaften des Rahmens
 * verteilen; zwei Komponenten müssten den Zustand irgendwo darüber halten, und „irgendwo
 * darüber" wäre App.tsx. Deshalb gibt dieser Hook die zwei fertigen Stücke zurück, und der
 * Zustand bleibt hier, wo er hingehört.
 *
 * **Was der Bereich NICHT ist: ein zweites Detail.** Rechts steht buchstäblich derselbe
 * Baustein wie im Angebote-Reiter der Akte (`components/AngebotDetail.tsx`), und angelegt
 * wird über denselben Hook (`components/angebotAnlegen.ts`) — samt „Anlegen und Vorlage
 * anwenden in einem Zug". Was hier hinzukommt, ist ausschliesslich das Drumherum: die
 * Objektliste, die Kundenauswahl vor dem Anlegen und der Kunde als fremdes Objekt (Text + Verweis „Zur Akte ‹Kunde›").
 *
 * **Der Kundenbezug.** Ein Angebot wird über SEINEN KUNDEN geschrieben
 * (`updateOffer(kundeId, …)`) und im Regelfall auch gelesen (`getOffer(kundeId, angebotId)`).
 * Die Kunden-Kennung steht in der Trefferzeile der Objektliste; von dort nimmt der Bereich
 * sie und merkt sie sich, solange derselbe Vorgang offen ist (`bezug`).
 *
 * **Der RÜCKFALL für Lesezeichen** (Entscheid Dirk 06.09.2026). Steht die adressierte
 * Kennung NICHT auf der Trefferseite — ein Lesezeichen, ein geteilter Link, ein Neuladen
 * nach einem Filterwechsel —, dann holt der Bereich den Satz über seine eigene Kennung
 * (`getOfferById`, GET /api/offers/{id}); die Antwort trägt den Kundenkontext mit. Bis
 * dahin stand hier die Auskunft „steht nicht in der Trefferliste", und die Adresse war
 * damit nur so lange gültig, wie die Spalte zufällig die passende Seite zeigte.
 *
 * **Und der nachgeladene Satz geht denselben Weg wie ein Listensatz — EINE Quelle.** Der
 * Rückfall füllt genau die beiden Zustände, die auch die Trefferzeile füllt (`bezug` und
 * `satz`), und ab da ist er von einem Listensatz nicht mehr zu unterscheiden: dieselbe
 * Versionsführung, dasselbe `onFortschreiben`, dasselbe `onNeuLaden`. Ein zweiter
 * Zustandspfad „nachgeladen" hätte genau die Fälle auseinanderlaufen lassen, die niemand
 * täglich durchspielt — Speichern aus einem Lesezeichen heraus zum Beispiel. Dasselbe tut
 * BereichRechnungen mit dem Beleg, den ihm der Auftrags-Bereich herüberreicht.
 *
 * Die dreistufige Auskunft bleibt für das, was der Rückfall NICHT heilen kann: „Lädt …",
 * „gibt es nicht (mehr)" (echte 404 — sie bedeutet auch „darf dieser Benutzer nicht sehen",
 * siehe routes/vorgangsLesezeichen.ts) und der Ladefehler.
 *
 * **BINDEND (Ruling im Baustein, AngebotDetail.tsx → `speichern`): Die Liste darf nicht
 * neu laden, solange ein Detail in Bearbeitung ist.** Die Version, mit der gespeichert
 * wird, stammt aus dem Satz, den dieser Bereich führt; ein Neuladen währenddessen ersetzte
 * sie unbemerkt durch die fremde und machte die optimistische Sperre wirkungslos. Deshalb
 * zweierlei: Der Satz wird NUR gelesen, wenn der Bereich ihn noch nicht hat oder wenn der
 * Baustein selbst darum bittet (`onNeuLaden`, das er erst nach `onSchliessen` ruft) — und
 * die Bedienelemente über der Liste (Suche, Filter, Blätterung, Zeilen) sind während der
 * Bearbeitung gesperrt, sodass sie gar nicht erst eine neue Abfrage auslösen können. Das
 * ist dieselbe Sperre, die der Akten-Reiter mit `gesperrt` über seine Liste legt.
 *
 * **CSS:** eigener Block `bang__` für das Drumherum. Was der Baustein mitbringt, behält
 * seine Klassen (`kang__` — herausgelöste Bestandsteile werden nicht umbenannt, Etappe 1,
 * Aufgabe 2); geteilt wird über Ansichten hinweg KEIN Block. Die ZEILE selbst trägt seit
 * dem 23.09.2026 den Block `ki-objekt__` des gemeinsamen Bausteins (packages/ui/src/liste/
 * Objektliste.css) und keinen eigenen mehr.
 */

/** Der Bereich braucht alles, was der Akten-Reiter braucht — plus die globale Suche und den
 *  Lesezeichen-Weg (`getOfferById`), den er als Rückfall geht. */
export type BereichAngeboteClient =
  AngeboteClient & Pick<ApiClient, "searchOffers" | "getOfferById">;

/** Die Kunden-Suchauswahl, die App.tsx ohnehin führt (dieselbe Liste, die im Kundenbereich
 *  in der Spalte steht). Der Bereich baut keine zweite — er zeichnet sie nur an einer
 *  anderen Stelle, nämlich als ersten Schritt des Anlegens. */
export type BereichAngeboteKunden = {
  items: KundeListeEintrag[];
  loading: boolean;
  error: KundenFehler | null;
  search: string;
  onSearch: (wert: string) => void;
};

export type BereichAngeboteProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Der Hook wird — Regeln der Hooks — auch
   *  an anderen Adressen gerufen; ohne diesen Riegel liefe seine Suche überall mit. */
  aktiv: boolean;
  client: BereichAngeboteClient;
  /** Die sichtbaren Konzernunternehmen: Unternehmens-Chips und Auflösung der Kürzel. */
  companies: Unternehmen[];
  /** Das aktive Unternehmen aus der Kopfleiste: Vorgabe beim Anlegen (ME-Etappe 1, Aufgabe 7)
   *  und der feste Chip der Unternehmens-Chips (Spec Mandantentrennung, R21). */
  arbeitsunternehmenId: string | null;
  /** Das geöffnete Angebot aus der Adresse (`/angebote/:id`) — `null` heisst „keins". */
  angebotId: string | null;
  /**
   * Die Zeile, die die Liste als gewählt zeigt, solange KEIN Objekt offen ist — am Telefon
   * die zuletzt geöffnete, damit der Rückweg die Auswahl behält (App.tsx, Hausregel
   * „Scroll bei Neuzeichnen"). Weggelassen: keine.
   */
  markiertId?: string | null;
  kunden: BereichAngeboteKunden;
  /** Adresswechsel innerhalb des Bereichs. Der Bereich navigiert NICHT selbst — die
   *  Adresse kennt App.tsx (dasselbe Muster wie bei den globalen Listen zuvor). */
  onOeffnen: (angebotId: string | null) => void;
  /** Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ in den Reiter „Dateien“ der Akte, in diesem Ordner. */
  onOrdnerOeffnen: (kundeId: string, folderId: string) => void;
  /**
   * Die Angebot→Auftrag-Naht (Ruling P3A-B, seit dem Ruling des Controllers vom 06.09.2026
   * BEREICHSINTERN geführt). Der Bereich erzeugt selbst KEINEN Auftrag — er meldet nur, aus
   * WELCHEM Angebot und für welchen Kunden einer entstehen soll; die Vorbelegung legt
   * App.tsx ab und der Auftrags-Bereich nimmt sie auf (views/BereichAuftraege.tsx).
   *
   * Der Kundenname reist mit, weil er hier schon dasteht (Trefferzeile): Der Auftrags-
   * Bereich überspringt seine Kundenwahl und käme sonst nur über einen zusätzlichen Abruf
   * an einen Namen, den dieser Bildschirm gerade anzeigt.
   */
  onAuftragAusAngebot: (kundeId: string, kundeName: string, angebot: Angebot) => void;
  /** Der gewählte Statusfilter aus der Adresse (`/angebote/<filter>`) — `null` heisst
   *  „alle Angebote". Einfach wählbar, weil der Server-Weg genau einen Status nimmt
   *  (siehe components/Filterkopf.tsx und bereichsfilter.ts). */
  filter: AngebotsFilter | null;
  /** Den Statusfilter wechseln. Er klappt das offene Angebot zu, weil es in der neuen
   *  Liste nicht stehen muss. */
  onFilter: (filter: AngebotsFilter | null) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten): Bei offener
   *  Bearbeitung fragt jede Navigation nach. Weglassbar — dann fragt niemand, was für
   *  einen Prüfharnisch ohne Router genau richtig ist. */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export type BereichAngeboteTeile = {
  /** Für `MeSchale.list` — Filterkopf, Angebotszeilen, Blätterung. */
  spalte: ReactElement;
  /** Für `MeSchale.detail` — das gewählte Angebot als Lese-Detail bzw. die Anlege-Maske. */
  detail: ReactElement;
  /**
   * Steht die Anlege-Maske offen? Auf dem iPhone muss der Rahmen dann das DETAIL zeigen,
   * obwohl die Adresse (`/angebote`) sonst die Spalte zuerst zeigt — die Maske hat keine
   * eigene Adresse (ein Lesezeichen auf ein offenes Formular wäre sinnlos, siehe router.ts).
   *
   * Sie war zwischen dem 07. und dem 23.09.2026 nicht da, weil es ohne Spalte nichts zu
   * falten gab. Mit der Spalte stellt sich die Frage wieder, und die Antwort ist dieselbe wie
   * vorher.
   */
  anlageOffen: boolean;
};

/** Die Nachfolge-Marken einer Trefferzeile: „ersetzt durch ‹Nr›“ aus `successor` (Festlegung 8) und „Treffer in
 *  v‹n›“, wenn der Suchtreffer in einer anderen als der gezeigten Version lag (W15). */
function successionMarks(a: AngebotTreffer): string[] {
  return [
    ...(a.successor === null ? [] : [SUCCESSION_TEXT.replacedBy(a.successor.displayNumber)]),
    ...(a.matchedVersionNumber === null || a.matchedVersionNumber === a.versionNumber
      ? [] : [SUCCESSION_TEXT.matchedVersion(a.matchedVersionNumber)]),
  ];
}

/** Wie viele Treffer auf eine Seite gehen — dieselbe Zahl wie bisher in der globalen Liste. */
const JE_SEITE = 20;

/** Die Chips der Filterleiste: die Statuswerte in der Reihenfolge des Lebenslaufs, mit
 *  ihren Beschriftungen aus der Beschriftungstabelle (vorgangsstatus.ts) — nicht abgetippt,
 *  damit ein umbenannter Status hier nicht ein zweites Mal gepflegt werden muss. */
const CHIPS: readonly FilterChip<AngebotsFilter>[] = ANGEBOTS_FILTER.map((f) => (
  { id: f.id, label: ANGEBOT_STATUS_LABEL[f.status] }));

/** Der Kundenbezug eines geöffneten Angebots — einmal aus der Trefferzeile aufgelöst und
 *  danach festgehalten, damit ein Filterwechsel in der Spalte das offene Detail nicht
 *  zuklappt (die Zeile fiele dann ja aus der Trefferseite). */
type Kundenbezug = { angebotId: string; kundeId: string; kundeName: string };

export function useBereichAngebote(p: BereichAngeboteProps): BereichAngeboteTeile {
  const { client, companies, aktiv, angebotId, filter } = p;

  const [seite, setSeite] = useState<AngebotTrefferSeite | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [zusatz, setZusatz] = useState<string[]>([]);
  /** Die Sicht (Spec Mandantentrennung, R6): das aktive Unternehmen samt den zugeschalteten —
   *  `undefined` heisst „nur das aktive", und dann fragt der Bereich ohne Parameter. */
  const sicht = useStabileListe(sichtFuer(p.arbeitsunternehmenId, zusatz));
  const [suche, setSuche] = useState("");
  /** Die Auswahl „Art“ (Aufgabe 26): adresslos wie der Suchtext, darum räumt sie das Verlassen mit ab. */
  const [art, setArt] = useState<KindFilter>("ALL");
  const [offset, setOffset] = useState(0);
  /** Hochgezählt, wenn die Trefferseite einen Stand noch nicht kennen kann — nach dem
   *  Anlegen und nach jeder Änderung, die der Baustein meldet. */
  const [listeVersion, setListeVersion] = useState(0);

  const [bezug, setBezug] = useState<Kundenbezug | null>(null);
  const [satz, setSatz] = useState<Angebot | null>(null);
  const [satzFehler, setSatzFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  /**
   * WELCHE Kennung der Rückfall als „gibt es nicht (mehr)" zurückgemeldet hat — als
   * Kennung, nicht als Ja/Nein.
   *
   * Dieselbe Überlegung wie bei `bearbeitetId` gleich darunter: Ein blosses Flag überlebte
   * den Adresswechsel und behauptete danach über den NÄCHSTEN Vorgang, es gebe ihn nicht.
   * Über die Kennung räumt sich der Zustand von selbst auf.
   */
  const [fehltId, setFehltId] = useState<string | null>(null);

  /**
   * WELCHER Vorgang gerade bearbeitet wird — als Kennung, nicht als Ja/Nein.
   *
   * Fixrunde 1: Ein blosses `bearbeitung: boolean` überlebte den Adresswechsel. Wer aus
   * der offenen Bearbeitung heraus zurückging (Rückweg im Kopf, Zurück-Taste), landete auf
   * der Objektliste — und die blieb für immer gesperrt, denn ihr Riegel hängt genau an
   * dieser Angabe und es gab keinen Bedienweg mehr, sie zu lösen. Über die Kennung ist der
   * Zustand an den geöffneten Vorgang GEBUNDEN: Ist keiner offen (oder ein anderer), ist
   * auch nichts in Bearbeitung. Dasselbe Muster führt der Akten-Reiter mit `bearbeitetId`.
   */
  const [bearbeitetId, setBearbeitetId] = useState<string | null>(null);
  const bearbeitung = bearbeitetId !== null && bearbeitetId === angebotId;

  /** Der Anlege-Weg: `null` = zu, sonst der Schritt (Kunde wählen bzw. Formular). */
  const [anlageKunde, setAnlageKunde] = useState<KundeListeEintrag | null>(null);
  const [anlageOffen, setAnlageOffen] = useState(false);
  /** Was angelegt wird: ein neues Angebot oder ein historisches (Aufgabe 26) — der erste Schritt (Kunde) ist derselbe. */
  const [anlageHistorisch, setAnlageHistorisch] = useState(false);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Fixrunde 1).
   *
   * Anlege-Maske und Meldungsstreifen sind Bearbeitungszustände ohne eigene Adresse. Blieben
   * sie über einen Menüwechsel hinweg stehen, käme man mit einer halb ausgefüllten Maske
   * zurück, die zu einer längst verlassenen Absicht gehört — und `anlageOffen` behauptete
   * währenddessen ausserhalb des Bereichs weiter, es stehe etwas im Detail (siehe
   * `showList` in App.tsx). Die Kennung des bearbeiteten Vorgangs räumt derselbe Effekt
   * mit ab, damit ein späterer Vorwärts-Sprung auf dieselbe Adresse nicht in einem
   * Formular landet, das niemand geöffnet hat.
   *
   * Und mit ihnen der adresslose Teil der FILTERLEISTE — Suchtext, Unternehmen,
   * Blätterung —, denn er ist genauso ein Bearbeitungszustand ohne eigene Adresse
   * (dieselbe Regel wie im Rechnungsbereich, wo sie zuerst gezogen wurde). Bliebe er
   * stehen, zeigte die Liste beim nächsten Betreten heimlich eine Teilmenge, ohne dass die
   * Adresse es sagte: Wer über das Menü zurückkommt, erwartet den Bereich, nicht den Rest
   * einer alten Suche. Der LADEFEHLER geht mit — ein Fehler von vorhin ist keine Auskunft
   * über den Bestand von jetzt.
   *
   * Der STATUSFILTER gehört seit dem 07.09.2026 NICHT mehr dazu: Er steht in der Adresse
   * und räumt sich damit von selbst ab — die Hauptnavigation führt auf `/angebote`, also
   * auf „alle". Ihn hier zusätzlich zurückzusetzen hiesse, den Zustand an zwei Stellen zu
   * führen.
   */
  useEffect(() => {
    if (aktiv) return;
    setAnlageOffen(false);
    setAnlageKunde(null);
    setBearbeitetId(null);
    setHinweis(null);
    setSuche("");
    setArt("ALL");
    setZusatz([]);
    setOffset(0);
    setLadeFehler(null);
  }, [aktiv]);

  const { bestaetigen } = useBestaetigung();

  /**
   * Verlassen-Wächter (Hausmuster, siehe KundeStammdaten): Solange ein Vorgang offen
   * bearbeitet wird, fragt JEDE Navigation nach — der Rückweg im Kopf des Einspalters
   * ebenso wie die Zurück-Taste. Ohne ihn verwarf ein Rückweg die Eingaben stumm.
   *
   * Registriert wird am OFFENEN Formular, nicht erst an einer erkannten Änderung: Der
   * Entwurf lebt im Baustein (components/AngebotDetail.tsx), und der Bereich sieht ihn
   * nicht. Ihn dafür nach aussen zu reichen hiesse, den Baustein für einen seiner beiden
   * Verwender umzubauen — die Rückfrage einmal zu viel ist der kleinere Preis als eine
   * Änderung, die still verloren geht.
   *
   * Bestätigtes Verlassen räumt von selbst auf: Die Adresse wechselt, und damit passt
   * `bearbeitetId` nicht mehr zum geöffneten Vorgang.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  /* Ebenso beim offenen ERFASSEN (Gesamtreview A-W3): Anlage-Maske und historisches Panel stehen ab der Kundenwahl, ihre
     Eingaben, gewählten Dateien und Upload-Ergebnisse gingen sonst bei jeder Navigation still verloren. */
  const creating = anlageOffen && anlageKunde !== null;
  /* Als Ref, die `anlageSchliessen` SOFORT löst (Re-Review A-W3): Der Sprung auf den neuen Vorgang nach dem Erfassen
     kommt im selben Zug wie das Schließen — der angemeldete Wächter sähe sonst noch die offene Erfassung und fragte. */
  const creatingRef = useRef(creating);
  creatingRef.current = creating;
  useEffect(() => {
    if ((!bearbeitung && !creating) || registriereVerlassenWaechter === undefined) return;
    return registriereVerlassenWaechter(() => (!bearbeitung && !creatingRef.current) || leaveQuestion(bearbeitung));
  }, [bearbeitung, creating, registriereVerlassenWaechter]);

  /** Die Frage des Wächters: beim Bearbeiten bzw. beim Erfassen. */
  function leaveQuestion(editing: boolean): Promise<boolean> {
    return bestaetigen({
      titel: LEAVE_TEXT.title,
      text: editing ? LEAVE_TEXT.offerEditing : LEAVE_TEXT.offerCreating,
      bestaetigenText: LEAVE_TEXT.confirm,
      gefaehrlich: true,
    });
  }

  /** Ein Weg aus der offenen Erfassung, der sie selbst schließt (Zeile wählen, Filter wechseln): ERST fragen, dann
   *  schließen und gehen — sonst stünde die Frage des Routers vor einer schon verworfenen Maske. Ohne offene Erfassung
   *  geht es ohne Frage im selben Zug. */
  function leaveCreation(go: () => void): void {
    if (!creatingRef.current) { go(); return; }
    void leaveQuestion(false).then((weiter) => {
      if (!weiter) return;
      anlageSchliessen();
      go();
    });
  }

  const searchOffers = client.searchOffers;
  /** Der Status hinter dem gewählten Filter — `undefined` heisst „alle" und geht gar nicht
   *  erst mit (ein leerer Wert wäre ein Filter auf „leer"). */
  const status = filter === null ? undefined : statusZuFilter(filter, ANGEBOTS_FILTER);

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    // Nur GESETZTE Filter gehen mit: Ein leerer Wert wäre ein Filter auf „leer".
    searchOffers({
      ...(status === undefined ? {} : { status }),
      ...kindParam(art),
      ...(sicht === undefined ? {} : { companyIds: sicht }),
      ...(suche === "" ? {} : { suche }),
      limit: JE_SEITE,
      offset,
    })
      .then((antwort) => { if (!abgebrochen) { setSeite(antwort); setLadeFehler(null); } })
      .catch(() => {
        if (!abgebrochen) {
          setLadeFehler("Die Angebote konnten nicht geladen werden. "
            + "Bitte die Seite neu laden.");
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, searchOffers, status, art, sicht, suche, offset, listeVersion]);

  /**
   * Den Kundenbezug des geöffneten Angebots aus der Trefferseite auflösen — EINMAL je
   * Vorgang. Steht er schon, bleibt er: Sonst risse ein Filterwechsel, der die Zeile aus
   * der Trefferseite drängt, das offene Detail wieder zu.
   */
  useEffect(() => {
    if (angebotId === null) { setBezug(null); return; }
    const zeile = seite?.items.find((t) => t.id === angebotId);
    setBezug((alt) => {
      if (alt !== null && alt.angebotId === angebotId) return alt;
      if (zeile === undefined) return null;
      return { angebotId, kundeId: zeile.customerId, kundeName: zeile.customerName };
    });
  }, [angebotId, seite]);

  /*
   * Den VOLLEN Vorgang lesen — die Trefferzeile trägt bewusst nur die magere Gestalt eines
   * Sucheinstiegs (kein `version`, keine Texte, keine Laufzeit).
   *
   * Der Riegel über `satzRef` ist die Umsetzung des Rulings aus dem Kopfkommentar: Gelesen
   * wird nur, was noch nicht da ist. Alles Weitere — nach dem Speichern, nach einem
   * Statuswechsel, nach einer 409 — stösst der Baustein selbst über `onNeuLaden` an, und
   * zwar erst, nachdem er die Bearbeitung geschlossen hat.
   */
  const satzRef = useRef<Angebot | null>(null);
  satzRef.current = satz;
  useEffect(() => {
    if (!aktiv || bezug === null) { setSatz(null); setSatzFehler(null); return; }
    if (satzRef.current !== null && satzRef.current.id === bezug.angebotId) return;
    let abgebrochen = false;
    setSatz(null);
    setSatzFehler(null);
    client.getOffer(bezug.kundeId, bezug.angebotId)
      .then((a) => { if (!abgebrochen) setSatz(a); })
      .catch((e) => {
        if (!abgebrochen) {
          setSatzFehler(apiMeldung(e, "Das Angebot konnte nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, client, bezug]);

  /*
   * DER RÜCKFALL für Lesezeichen (siehe den Kopf dieser Datei): Was die Trefferseite nicht
   * trägt, wird über die eigene Kennung geholt — samt Kundenkontext.
   *
   * **Die drei Bedingungen sind alle drei nötig**, und jede fehlende erzeugte einen echten
   * Fehler:
   *
   *  - `bezugSteht` — sonst liefe der Rückfall auch dann, wenn der Kundenbezug längst da
   *    ist, und überschriebe den geführten Satz mitten in der Bearbeitung.
   *  - `zeileVorhanden` — sonst schösse er bei JEDEM Zeilenklick los: Beim Klick ist
   *    `angebotId` schon neu, während `bezug` in diesem Durchlauf noch das alte trägt. Der
   *    Rückfall führe dann parallel zur Auflösung aus der Zeile, und beide setzten `bezug`.
   *  - `listeSteht` — solange die Suche läuft, ist noch gar nicht entschieden, ob die Zeile
   *    fehlt. Ist die Suche GESCHEITERT, gilt sie als entschieden: Ohne Liste kommt der
   *    Bezug sicher nicht mehr von dort, und genau dann ist der Rückfall der einzige Weg
   *    zum Vorgang (die gescheiterte Liste meldet die Spalte selbst).
   *
   * Die STELLUNG nach dem Satz-Lade-Effekt darüber ist bindend, aus dem Grund, den
   * BereichRechnungen an derselben Naht ausschreibt: Jener räumt `satz` ab, solange es
   * keinen Bezug gibt, und Effekte EINES Durchlaufs werden gemeinsam angewandt.
   */
  const bezugSteht = bezug !== null && bezug.angebotId === angebotId;
  const zeileVorhanden = seite !== null && seite.items.some((t) => t.id === angebotId);
  const listeSteht = seite !== null || ladeFehler !== null;
  useEffect(() => {
    if (!aktiv || angebotId === null || bezugSteht || zeileVorhanden || !listeSteht) return;
    let abgebrochen = false;
    // Vor dem Versuch beide Auskünfte des VORIGEN zurücknehmen: Wer den Bereich verlässt und
    // auf dieselbe Adresse zurückkommt, läuft hier erneut durch — und sähe sonst „gibt es
    // nicht (mehr)" weiterstehen, während der neue Versuch noch läuft.
    setFehltId(null);
    setSatzFehler(null);
    client.getOfferById(angebotId)
      .then((a) => {
        if (abgebrochen) return;
        // GENAU die beiden Zustände, die auch eine Trefferzeile füllt — ab hier ist der
        // Satz von einem Listensatz nicht mehr zu unterscheiden (EINE Quelle).
        setBezug({ angebotId: a.id, kundeId: a.customerId, kundeName: a.customerName });
        setSatz(a);
      })
      .catch((e) => {
        if (abgebrochen) return;
        // Die 404 heisst „gibt es nicht ODER darfst du nicht sehen" — die API unterscheidet
        // das bewusst nicht, und die Auskunft hier tut es deshalb auch nicht.
        if (e instanceof ApiError && e.status === 404) setFehltId(angebotId);
        else setSatzFehler(apiMeldung(e, "Das Angebot konnte nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [aktiv, client, angebotId, bezugSteht, zeileVorhanden, listeSteht]);

  /** Der Baustein hat den Vorgang weitergedreht (Version/Betrag aus dem Positions-Editor,
   *  frischer Kopf nach „Vorlage anwenden"). Er bleibt HIER stehen — kein Server-Abruf,
   *  siehe das Ruling im Kopf. */
  function fortschreiben(neu: Angebot): void {
    setSatz(neu);
  }

  /**
   * Der Baustein hat den Vorgang durch eine ANDERE Kennung ERSETZT — „Neue Version" oder
   * „Kopieren" (Review-Befund Fixrunde 1). Anders als `fortschreiben` bleibt hier NICHT
   * dieselbe Kennung stehen: `bezug` UND `satz` gehen auf den neuen Vorgang über, und die
   * ADRESSE zieht mit (`p.onOeffnen`) — Global Constraint „der Rahmen lädt seine Liste neu
   * und öffnet die neue Version".
   *
   * Kein zweiter Abruf nötig: Der neue Satz kam gerade erst vom Server zurück (Antwort von
   * `neueOfferVersion`/`kopiereOffer`), genau wie bei `fortschreiben`. Ein `satzNeuLaden()`
   * an dieser Stelle läse dagegen mit der ALTEN, in `bezug` noch stehenden Kennung — und
   * damit die jetzt SUPERSEDED Fassung zurück, die die eben angelegte neue Version sofort
   * wieder überschriebe (der eigentliche Fehlschlag dieser Fixrunde).
   *
   * Die Trefferseite zieht über `listeVersion` trotzdem nach: Sie soll den neuen Stand
   * (die neue Zeile, den abgelösten alten Status) zeigen, sobald sie das nächste Mal
   * gelesen wird.
   */
  function ersetzt(neu: Angebot): void {
    if (bezug === null) return;
    setBezug({ angebotId: neu.id, kundeId: bezug.kundeId, kundeName: bezug.kundeName });
    setSatz(neu);
    setSatzFehler(null);
    setListeVersion((v) => v + 1);
    p.onOeffnen(neu.id);
  }

  /** Der Baustein bittet um einen frischen Stand. Erst dann — und nur dann — wird der
   *  Vorgang neu gelesen und die Trefferseite nachgezogen. */
  async function satzNeuLaden(): Promise<void> {
    if (bezug === null) return;
    setListeVersion((v) => v + 1);
    try {
      setSatz(await client.getOffer(bezug.kundeId, bezug.angebotId));
      setSatzFehler(null);
    } catch (e) {
      setSatzFehler(apiMeldung(e, "Das Angebot konnte nicht geladen werden."));
    }
  }

  function anlageSchliessen(): void {
    creatingRef.current = false;
    setAnlageOffen(false);
    setAnlageKunde(null);
  }

  function anlageBeginnen(historisch = false): void {
    setHinweis(null);
    setAnlageKunde(null);
    setAnlageHistorisch(historisch);
    setAnlageOffen(true);
    // Der bisher geöffnete Vorgang wird auch in der ADRESSE zugeklappt: Sonst stünde
    // `/angebote/<id>` über einer Maske, die einen anderen Vorgang anlegt, und die Spalte
    // hübe eine Zeile hervor, die gerade gar nicht zu sehen ist.
    p.onOeffnen(null);
  }

  /** Nach dem Anlegen mit Vorlage: den neuen Vorgang gleich aufschlagen (Dirks
   *  Muss-Feature — wählen, dann nur noch die Mengen füllen). */
  function neuBearbeiten(kunde: KundeListeEintrag, a: Angebot, meldung: string): void {
    setBezug({ angebotId: a.id, kundeId: kunde.id, kundeName: kunde.companyName });
    setSatz(a);
    setBearbeitetId(a.id);
    setHinweis(meldung);
    p.onOeffnen(a.id);
  }

  /** Das historische Angebot steht: Liste nachziehen und es gleich aufschlagen (Kundenbezug aus der Anlage). */
  function historischAngelegt(kunde: KundeListeEintrag, id: string): void {
    anlageSchliessen();
    setListeVersion((v) => v + 1);
    setBezug({ angebotId: id, kundeId: kunde.id, kundeName: kunde.companyName });
    p.onOeffnen(id);
  }

  /** Filter ändern heisst: zurück auf die erste Seite. Sonst stünde man nach dem Filtern
   *  auf Seite 7 eines Ergebnisses mit einer Seite und sähe nichts. */
  function filtern(setzen: () => void): void {
    setzen();
    setOffset(0);
  }

  /**
   * Den STATUSFILTER wechseln. Er steht in der Adresse, also navigiert der Bereich nicht
   * selbst — App.tsx tut es (`onFilter`) und klappt dabei das offene Angebot zu: Es muss in
   * der neuen Liste nicht stehen.
   *
   * Der SUCHTEXT bleibt ausdrücklich stehen: „Wo steht das Angebot für X?" ist eine Frage,
   * die man durch die Gruppen HINDURCH stellt. Die Blätterung geht auf Seite 1 zurück.
   */
  function filterWaehlen(neu: AngebotsFilter | null): void {
    leaveCreation(() => {
      zuklappen();
      p.onFilter(neu);
    });
  }

  /** Die Unternehmens-Chips wechseln (Spec Mandantentrennung R21): wie ein Filterwechsel,
   *  und dazu klappt das offene Objekt zu — es muss in der neuen Sicht nicht stehen. Die
   *  Adresse geht auf die Liste; ein Statusfilter bleibt stehen. */
  function chipsWaehlen(ids: string[]): void {
    leaveCreation(() => {
      zuklappen();
      setZusatz(ids);
      p.onOeffnen(null);
    });
  }

  /** Der gemeinsame Teil von Statusfilter- und Chipwechsel: Hinweis, Bearbeitung und Anlage
   *  weg, zurück auf die erste Seite. */
  function zuklappen(): void {
    setHinweis(null);
    setBearbeitetId(null);
    anlageSchliessen();
    setOffset(0);
  }

  /** Das Kürzel des Unternehmens zu einer Zeile. Fehlt die Firma (sie könnte stillgelegt
   *  sein), bleibt die Stelle leer statt eine nackte UUID zu zeigen. */
  function kuerzel(id: string): string {
    return companies.find((c) => c.id === id)?.shortCode ?? "";
  }

  const gefiltert = filter !== null || zusatz.length > 0 || suche !== "" || art !== "ALL";
  const von = seite === null || seite.total === 0 ? 0 : offset + 1;
  const bis = seite === null ? 0 : Math.min(offset + seite.items.length, seite.total);

  const spalte = (
    <div className="bang">
      {/*
        Der FILTERKOPF (components/Filterkopf.tsx) — Suchfeld, Status-Chips und die
        Unternehmens-Chips (Mandantentrennung R21), in einem Baustein, den alle vier
        Objektbereiche teilen. Die Chips sind EINFACH wählbar: Der Server-Weg dieses
        Bereichs nimmt genau einen Status (siehe bereichsfilter.ts).

        `lage="spalte"` seit dem 23.09.2026 (Entscheid Dirk): Er steht jetzt oben in der
        Listenspalte des Rahmens und nicht mehr über dem Inhalt. Suchfeld und Anlegen-Knopf
        stehen in einer Zeile, Chips und Auswahllisten brechen um — das konnten sie schon.
      */}
      <Filterkopf
        lage="spalte"
        suchLabel="Angebote durchsuchen"
        platzhalter="Nummer, Titel oder Kunde"
        suche={suche}
        onSuche={(t) => filtern(() => setSuche(t))}
        gesperrt={bearbeitung}
        chips={{
          label: "Angebote nach Status",
          alle: "Alle Angebote",
          werte: CHIPS,
          modus: "einfach",
          gewaehlt: filter,
          onGewaehlt: filterWaehlen,
        }}
        aktion={
          <>
            <KiKnopf art="primaer" className="bang__neu" onClick={() => anlageBeginnen()}
              deaktiviert={bearbeitung || anlageOffen}>
              Neues Angebot
            </KiKnopf>
            <KiKnopf onClick={() => anlageBeginnen(true)} deaktiviert={bearbeitung || anlageOffen}>
              {HISTORICAL_TEXT.create.offer}
            </KiKnopf>
          </>
        }
        zusatz={
          <>
            <UnternehmensChips label="Angebote nach Unternehmen" companies={companies}
              aktivId={p.arbeitsunternehmenId} zusatz={zusatz} gesperrt={bearbeitung}
              onZusatz={chipsWaehlen} />
            <KindFilterSelect id="bang-art" value={art} disabled={bearbeitung}
              onChange={(w) => leaveCreation(() => { zuklappen(); setArt(w); p.onOeffnen(null); })} />
          </>
        }
      />

      {/* Die ANGEBOTSLISTE. Sie ROLLT (siehe BereichAngebote.css): Der Filterkopf bleibt
          oben stehen — sonst müsste man in einer Spalte mit zwanzig Zeilen an allen vorbei,
          um den Filter zu wechseln. */}
      <div className="bang__liste">
        {ladeFehler !== null && (
          <Hinweis art="fehler">{ladeFehler}</Hinweis>
        )}

        {ladeFehler === null && seite === null && (
          <p className="bang__lade" role="status">Lädt …</p>
        )}

        {ladeFehler === null && seite !== null && seite.items.length === 0 && (
          <p className="bang__hinweis">
            {gefiltert
              ? "Zu diesen Filtern gibt es kein Angebot."
              : "Es gibt noch keine Angebote."}
          </p>
        )}

        {/*
          Die Zeilen tragen den gemeinsamen Baustein (Entscheid Dirk 23.09.2026) — dieselbe
          Anatomie wie in den drei Schwesterbereichen und in den Objektlisten der Verwaltung.

          Was sich gegenüber der selbstgezeichneten Zeile ändert, ist die ANORDNUNG und nicht
          der Inhalt: Nummer und Version als `kennung`, Titel als `name`, der Status als
          Chip, und Kunde · Kürzel · Datum · Betrag als EIN Meta-Text. Die Versionspille wird
          Teil der Kennung — eine zweite Pille neben Nummer und Status-Chip hätte die erste
          Zeile auf einer 280 Pixel breiten Spalte dreifach umbrechen lassen.

          Der zugängliche Name endet auf „anzeigen" und nicht auf „öffnen": Der Klick
          adressiert das LESE-Detail, geändert wird erst über „Ändern" darin.
        */}
        <Objektliste label="Angebote">
          {(seite?.items ?? []).map((a) => (
            <Objektzeile
              key={a.id}
              name={a.title}
              /* Die Version erst ab 2 — die Liste zeigt ohnehin je Reihe nur die jüngste,
                 „v1" wäre an jeder Zeile dasselbe Wort. Nachgebildet wie im Detail, weil
                 `web` nicht aus `domain` einführen darf. */
              kennung={a.versionNumber >= 2
                ? `${a.displayNumber} v${a.versionNumber}` : a.displayNumber}
              /* Ein leeres Kürzel (die Firma ist archiviert und fehlt darum in
                 `companies` — /api/session liefert nur aktive Unternehmen) darf keinen
                 leeren Trenner „·  ·" hinterlassen, darum die Teile filtern statt
                 aneinanderzuhängen (Befund Gesamtreview, 23.09.2026). */
              meta={[
                a.customerName,
                kuerzel(a.companyId),
                formatDatum(a.createdAt),
                formatBetrag(a.netAmount, "EUR"),
              ].filter((t) => t !== "").join(" · ")}
              status={{ text: ANGEBOT_STATUS_LABEL[a.status], art: ANGEBOT_STATUS_ART[a.status] }}
              /* Kopieren und Nachfolge (Aufgabe 27): Die ersetzte Reihe tritt über me-design zurück (`zustand=
                 "ersetzt"`, Entscheid 97 / MW6 — gedimmt bis auf Chips, aber bedienbar); ihre Marke nennt den
                 Nachfolger (Festlegung 8). Lag der Suchtreffer in einer älteren Version, sagt es eine zweite Marke
                 (W15). Beide sind Kennzeichen: rahmenlose me-chips, nicht anklickbar. */
              ersetzt={a.replaced}
              marken={successionMarks(a)}
              /* Mit Marke bricht die Zeile um, statt die Kennung bis auf ein Zeichen zu drücken (A-W4): Nummer und
                 Marke behalten ihre Breite, die Meta-Zeile darunter. */
              umbruch={successionMarks(a).length > 0}
              gewaehlt={a.id === (angebotId ?? p.markiertId ?? null)}
              gesperrt={bearbeitung}
              ariaLabel={`Angebot ${a.displayNumber}, ${a.customerName}, anzeigen`}
              onOeffnen={() => leaveCreation(() => { setHinweis(null); anlageSchliessen(); p.onOeffnen(a.id); })}
            >
              {a.kind === "HISTORICAL" && <HistoricalChip />}
            </Objektzeile>
          ))}
        </Objektliste>

        {seite !== null && seite.total > 0 && (
          <div className="bang__blaettern">
            <KiKnopf className="bang__knopf"
              deaktiviert={offset === 0 || bearbeitung}
              onClick={() => setOffset((o) => Math.max(0, o - JE_SEITE))}>
              Zurück
            </KiKnopf>
            <span className="bang__zusatz">{von}–{bis} von {seite.total}</span>
            <KiKnopf className="bang__knopf"
              deaktiviert={offset + seite.items.length >= seite.total || bearbeitung}
              onClick={() => setOffset((o) => o + JE_SEITE)}>
              Weiter
            </KiKnopf>
          </div>
        )}
      </div>
    </div>
  );

  /*
   * Die Detailfläche bekommt bewusst KEINE eigene Landmarke: Der Rahmen zeichnet dort bereits
   * eine (MeSchale), und eine zweite Region namens „Angebote" wäre mit der Spalte daneben
   * verwechselbar. Was hier je nach Zustand steht — Anlege-Maske, Kundenwahl —, trägt seine
   * eigene Beschriftung.
   */
  const detail = (
    <div className="bang__inhalt">
      {/* Schlechtestes oben (Hausregel): der Fehler vor dem Hinweis. */}
      {satzFehler !== null && <Hinweis art="fehler">{satzFehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="info">{hinweis}</Hinweis>}

      {anlageOffen ? (
        anlageKunde === null ? (
          <section className="bang__anlage" aria-label="Kunde wählen">
            <h2 className="bang__anlage-titel">
              {anlageHistorisch ? HISTORICAL_TEXT.create.offer : "Neues Angebot"}
            </h2>
            <p className="bang__anlage-schritt">
              Ein Angebot gehört zu einem Kunden — bitte zuerst den Kunden wählen.
            </p>
            <div className="bang__kundenwahl">
              <KundenListe
                items={p.kunden.items}
                loading={p.kunden.loading}
                error={p.kunden.error}
                search={p.kunden.search}
                onSearch={p.kunden.onSearch}
                selectedId={null}
                onSelect={(id) => {
                  const gewaehlt = p.kunden.items.find((k) => k.id === id) ?? null;
                  setAnlageKunde(gewaehlt);
                }}
                companies={companies}
              />
            </div>
            <div className="kang__formular-aktionen">
              <KiKnopf className="kang__sekundaer" onClick={anlageSchliessen}>
                Abbrechen
              </KiKnopf>
            </div>
          </section>
        ) : anlageHistorisch ? (
          <HistoricalCreateForCustomer client={client} kundeId={anlageKunde.id} art="offer"
            firmen={companies.filter((c) => anlageKunde.relations.some((r) => r.companyId === c.id))}
            onAbbrechen={anlageSchliessen} onGespeichert={(id) => historischAngelegt(anlageKunde, id)} />
        ) : (
          <AngebotAnlage
            client={client}
            kunde={anlageKunde}
            companies={companies}
            arbeitsunternehmenId={p.arbeitsunternehmenId}
            onAbbrechen={anlageSchliessen}
            onAnderenKunden={() => setAnlageKunde(null)}
            onAngelegt={() => { anlageSchliessen(); setListeVersion((v) => v + 1); }}
            onNeuLaden={async () => {
              setListeVersion((v) => v + 1);
              try {
                const { items } = await client.listOffers(anlageKunde.id);
                return items;
              } catch {
                return [];
              }
            }}
            onBearbeiten={(a, meldung) => neuBearbeiten(anlageKunde, a, meldung)}
          />
        )
      ) : angebotId === null ? (
        <p className="bang__leer">
          Links ein Angebot wählen — es steht dann hier, mit allem, was auch
          die Kundenakte zeigt.
        </p>
      ) : bezug === null ? (
        /*
         * Ohne Kundenbezug ist der Rückfall unterwegs oder gescheitert (siehe den Kopf
         * dieser Datei) — die dreistufige Auskunft:
         *
         *  - „gibt es nicht (mehr)" NUR bei echter 404. Sie deckt beides ab, was der
         *    Server dort zusammenfasst: gelöscht/nie dagewesen und „nicht sichtbar".
         *  - Der Ladefehler steht bereits als Alarm über dieser Fläche (`satzFehler`); hier
         *    bleibt die Stelle leer, statt die Meldung zweimal zu zeigen.
         *  - Sonst läuft noch etwas: die Suche, der Rückfall, oder beides.
         */
        fehltId === angebotId ? (
          <p className="bang__leer">
            Dieses Angebot gibt es nicht (mehr) — oder es gehört zu einem Unternehmen,
            das hier nicht sichtbar ist.
          </p>
        ) : satzFehler !== null ? (
          <></>
        ) : (
          <p className="bang__leer">Lädt …</p>
        )
      ) : satz === null ? (
        satzFehler === null ? <p className="bang__leer">Lädt …</p> : <></>
      ) : (
        <AngebotBereichsDetail
          client={client}
          kundeId={bezug.kundeId}
          kundeName={bezug.kundeName}
          angebot={satz}
          companies={companies}
          bearbeitung={bearbeitung}
          onBearbeiten={() => { setHinweis(null); setBearbeitetId(bezug.angebotId); }}
          onSchliessen={() => setBearbeitetId(null)}
          onNeuLaden={satzNeuLaden}
          onHinweis={setHinweis}
          onFortschreiben={fortschreiben}
          onErsetzt={ersetzt}
          onAuftragAusAngebot={(a) =>
            p.onAuftragAusAngebot(bezug.kundeId, bezug.kundeName, a)}
          onOrdnerOeffnen={(folderId) => p.onOrdnerOeffnen(bezug.kundeId, folderId)}
        />
      )}
    </div>
  );

  return { spalte, detail: detail, anlageOffen };
}

/**
 * Das Detail eines Angebots im Bereich: der Kundenbezug als Verweis, darunter der
 * BAUSTEIN — derselbe, den der Angebote-Reiter der Akte zeichnet.
 *
 * Eigene Komponente, damit `useAngebotsUmfeld` erst läuft, wenn es einen Kunden GIBT: Der
 * Hook zieht Ansprechpartner, Benutzer, Platzhalter und Vorlagen, und ohne Kunden-Kennung
 * wären das vier Abrufe ins Leere.
 */
function AngebotBereichsDetail(p: {
  client: BereichAngeboteClient;
  kundeId: string;
  kundeName: string;
  angebot: Angebot;
  companies: Unternehmen[];
  bearbeitung: boolean;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  onNeuLaden: () => Promise<void>;
  onHinweis: (text: string | null) => void;
  onFortschreiben: (angebot: Angebot) => void;
  onErsetzt: (angebot: Angebot) => void;
  onAuftragAusAngebot: (angebot: Angebot) => void;
  onOrdnerOeffnen: (folderId: string) => void;
}): ReactElement {
  const umfeld = useAngebotsUmfeld(p.client, p.kundeId, p.angebot.companyId);

  return (
    <div className="bang__karte">
      {/* Der Kopf der Detailkarte: der Kundenbezug. Der Rückweg „Zur Liste" stand hier
          zwischen dem 07. und dem 23.09.2026 — mit der Spalte faltet wieder der Rahmen, und
          er hat seinen Rückweg im Kopf (App.tsx, `zurueckWeg`). Zwei Rückwege übereinander
          wären zwei Antworten auf dieselbe Frage. */}
      <div className="bang__detail-kopf">
        {/* Der Kunde ist ein fremdes Objekt (115, „Öffnen statt Springen“ E2–E4, K18): Name als Text, der Sprung in die
            Akte direkt dahinter („Zur Akte ‹Kunde›“). Der Rückweg von dort kennt seine Herkunft (`sprungZiel`, App.tsx
            `zurueckWeg`). */}
        <span className="bang__kunde">{p.kundeName}</span>
        <ObjektVerweis bezug={{ art: "akte", id: p.kundeId, reiter: "uebersicht", label: p.kundeName }} />
      </div>

      <AngebotDetail
        // Der Wechsel der Kennung (Neue Version, Kopieren, ein neu gewählter Treffer)
        // hängt den Baustein NEU auf — Review-Befund Fixrunde 1: Kein UI-Zustand des
        // vorigen Vorgangs (PDF-Meldung, aufgeklappter Kopf, Versionsliste …) soll den
        // Wechsel überleben, und ein Wiederverwenden über blosse Props kann das nicht
        // garantieren, weil ein Teil dieses Zustands nur beim ERSTEN Aufbau gebildet wird.
        key={p.angebot.id}
        client={p.client}
        kundeId={p.kundeId}
        angebot={p.angebot}
        companies={p.companies}
        umfeld={umfeld}
        bearbeitung={p.bearbeitung}
        // Es gibt hier genau EIN Detail — was der Reiter mit `gesperrt` gegen seine
        // übrigen Zeilen absichert, kann im Bereich gar nicht auftreten.
        gesperrt={false}
        kundeName={p.kundeName}
        onBearbeiten={p.onBearbeiten}
        onSchliessen={p.onSchliessen}
        onNeuLaden={p.onNeuLaden}
        onHinweis={p.onHinweis}
        onFortschreiben={p.onFortschreiben}
        onErsetzt={p.onErsetzt}
        onAuftragAusAngebot={p.onAuftragAusAngebot}
        onOrdnerOeffnen={p.onOrdnerOeffnen}
        // Hier steht der Vorgang allein: Ein historischer zeigt Notiz und Dateien offen (Aufgabe 26).
        standalone />
    </div>
  );
}

/**
 * Der zweite Schritt des Anlegens: dieselbe Maske wie im Akten-Reiter, geführt vom
 * gemeinsamen Hook (`useAngebotAnlegen`) — samt „Anlegen und Vorlage anwenden in EINEM
 * Zug" und dem Aufschlagen des neuen Vorgangs, wenn eine Vorlage im Spiel war.
 *
 * Auch sie ist eine eigene Komponente, und aus demselben Grund wie das Detail: Erst mit
 * einem gewählten Kunden gibt es etwas zu laden.
 */
function AngebotAnlage(p: {
  client: BereichAngeboteClient;
  kunde: KundeListeEintrag;
  companies: Unternehmen[];
  arbeitsunternehmenId: string | null;
  onAbbrechen: () => void;
  onAnderenKunden: () => void;
  onAngelegt: () => void;
  onNeuLaden: () => Promise<Angebot[]>;
  onBearbeiten: (angebot: Angebot, meldung: string) => void;
}): ReactElement {
  // Belege entstehen im aktiven Unternehmen (Spec Mandantentrennung, E368): wählbar ist nur
  // das aktive, und nur, wenn der Kunde dort eine Beziehung hat (verbunden über die
  // `companyId`, wie in KundenAkte.tsx). Damit ist es vorgewählt; das Feld „Unternehmen" entfällt.
  const kundenUnternehmen = p.companies.filter((c) => c.id === p.arbeitsunternehmenId
    && p.kunde.relations.some((r) => r.companyId === c.id));

  const anlage = useAngebotAnlegen({
    client: p.client,
    kundeId: p.kunde.id,
    companies: kundenUnternehmen,
    arbeitsunternehmenId: p.arbeitsunternehmenId,
    feldPraefix: "bang-neu",
    onSchliessen: p.onAngelegt,
    onNeuLaden: p.onNeuLaden,
    onBearbeiten: p.onBearbeiten,
  });
  const { entwurf, setEntwurf } = anlage;

  const umfeld = useAngebotsUmfeld(p.client, p.kunde.id, entwurf.companyId);

  /**
   * Der EINE Ausgang neben dem Anlegen (Hausregel „striktes Modalpanel", 06.09.2026): kein
   * Ausgang unter laufendem Schreiben, Rückfrage, sobald etwas im Formular steht. Die
   * Vergleichsgrundlage führt der Anlege-Hook (`anlage.ausgang`).
   *
   * „Anderen Kunden wählen" geht denselben Weg: Es verlässt die Maske genauso — nur zurück
   * auf den ersten Schritt statt ganz hinaus.
   */
  async function abbrechen(weiter: () => void): Promise<void> {
    if (anlage.speichert) return;
    if (!(await darfAbbrechen(!entwuerfeGleich(entwurf, anlage.ausgang)))) return;
    weiter();
  }

  return (
    <Modal titel={`Neues Angebot für ${p.kunde.companyName}`} breite="weit"
      abbrechenGesperrt={anlage.speichert}
      onAbbrechen={() => abbrechen(p.onAbbrechen)}>
      {/* Der Abschnitt behält seinen Namen: Er ist die Maske IM Panel. Den Rahmen bringt
          seit dem 06.09.2026 das Panel mit — die Zusatzklasse nimmt ihn hier weg, während der
          ERSTE Schritt (Kunde wählen) ihn im Inhalt weiter trägt. */}
      <section className="bang__anlage bang__anlage--im-panel" aria-label="Neues Angebot">
        <div className="bang__anlage-kopf">
          <KiKnopf className="kang__sekundaer" deaktiviert={anlage.speichert}
            onClick={() => abbrechen(p.onAnderenKunden)}>
            Anderen Kunden wählen
          </KiKnopf>
        </div>

        {kundenUnternehmen.length === 0 && (
          <Hinweis art="warnung">
            Dieser Kunde hat keine Beziehung zum aktiven Unternehmen.
          </Hinweis>
        )}

        {/* DER ERSTE SCHRITT — wie in der Akte: „für ein neues Angebot wählt man einfach eine
            benannte Vorlage aus und füllt nur die Mengen ein". Deshalb steht die Wahl VOR den
            Kopffeldern; „ohne Vorlage" bleibt die Vorauswahl. */}
        <div className="bang__vorlagenwahl">
          <Auswahl id="bang-neu-vorlage" name="bang-neu-vorlage" beschriftung="Vorlage"
            wert={entwurf.vorlageId} gesperrt={anlage.speichert}
            onGeaendert={(d) => setEntwurf((v) => ({ ...v, vorlageId: d.wert }))}>
            <option value="">— ohne Vorlage —</option>
            {umfeld.vorlagen.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} ({v.itemCount} {v.itemCount === 1 ? "Position" : "Positionen"})
              </option>
            ))}
          </Auswahl>
          <p className="kang__vorlagen-hinweis">
            Mit einer Vorlage stehen die Positionen sofort im neuen Angebot — samt Texten,
            Periodenregel und Zahlplan. Dann nur noch die Mengen füllen.
          </p>
        </div>

        {/* Ohne Positionsblock — wie in der Akte: Am noch nicht angelegten Angebot gibt es
            keinen Vorgang, an dem Positionen hängen könnten. */}
        {angebotFormularFelder({
          praefix: "bang-neu", mitUnternehmen: false, companies: kundenUnternehmen, umfeld,
          entwurf, setEntwurf, hatPositionen: false, feldFehler: anlage.feldFehler,
          fehlerJeFeld: anlage.fehlerJeFeld, formular: { laeuft: anlage.speichert },
          faltungen: anlage.faltungen,
          vorschauWerte: angebotVorschauWerte({
            entwurf, companies: kundenUnternehmen, kontakte: umfeld.kontakte, angebot: null,
            kundeName: p.kunde.companyName,
          }),
        })}

      </section>

      <ModalFuss speichernText="Anlegen" speichernGesperrt={anlage.speichert}
        abbrechenGesperrt={anlage.speichert}
        onSpeichern={() => { void anlage.anlegen(); }}
        onAbbrechen={() => abbrechen(p.onAbbrechen)} />
    </Modal>
  );
}
